// Giao diện sáng/tối do người dùng chọn: Theo hệ thống | Sáng | Tối, lưu theo trình duyệt (localStorage). Lựa chọn áp lên
// <html data-theme> — CSS đã có nhánh :root[data-theme=…] (base.css); index.html áp sẵn trước khi vẽ để không nháy.
// Logo chọn theo giao diện đang hiệu lực (pickLogo).
import { useCallback, useSyncExternalStore } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

/** Trùng khoá trong đoạn script của index.html. */
export const THEME_KEY = 'cathouse.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Lựa chọn chưa lưu được (bộ nhớ trình duyệt bị chặn / đầy) ⇒ giữ trong lần mở này để nút, giao diện và logo cùng một lựa chọn. */
let unsaved: ThemeChoice | null = null;

export function readThemeChoice(): ThemeChoice {
  if (unsaved) return unsaved;
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system'; // bộ nhớ trình duyệt bị chặn (chế độ riêng tư, chính sách)
  }
}

export function saveThemeChoice(choice: ThemeChoice) {
  try {
    if (choice === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, choice);
    unsaved = null;
  } catch {
    unsaved = choice === 'system' ? null : choice;
  }
}

/** Màu thanh trình duyệt (điện thoại) theo nền trang của từng giao diện — trùng --bg của base.css và index.html. */
export const THEME_COLOR: Record<Theme, string> = { light: '#f6f6f5', dark: '#161618' };

export function applyTheme(choice: ThemeChoice) {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  // index.html có hai thẻ theme-color theo media (sáng / tối của máy): chọn tường minh ⇒ cả hai mang màu đã chọn
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((m) => {
    const own = (m.getAttribute('media') ?? '').includes('dark') ? THEME_COLOR.dark : THEME_COLOR.light;
    m.content = choice === 'system' ? own : THEME_COLOR[choice];
  });
}

const media = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null);
const systemTheme = (): Theme => (media()?.matches ? 'dark' : 'light');

// Kho nhỏ dùng chung: mọi useTheme cập nhật cùng lúc khi người dùng chọn, khi máy đổi sáng/tối, hoặc tab khác đổi lựa chọn.
const subscribers = new Set<() => void>();
let snapshot = '';
const emit = () => subscribers.forEach((cb) => cb());
function subscribe(cb: () => void) {
  subscribers.add(cb);
  const mq = media();
  mq?.addEventListener('change', cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== THEME_KEY) return;
    applyTheme(readThemeChoice());
    cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    subscribers.delete(cb);
    mq?.removeEventListener('change', cb);
    window.removeEventListener('storage', onStorage);
  };
}
/** Chuỗi ổn định "lựa chọn|hiệu lực" để useSyncExternalStore so sánh. */
function getSnapshot() {
  const choice = readThemeChoice();
  const next = `${choice}|${choice === 'system' ? systemTheme() : choice}`;
  if (next !== snapshot) snapshot = next;
  return snapshot;
}

export function useTheme() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, () => 'system|light');
  const [choice, theme] = snap.split('|') as [ThemeChoice, Theme];
  const setChoice = useCallback((c: ThemeChoice) => {
    saveThemeChoice(c);
    applyTheme(c);
    emit();
  }, []);
  return { choice, theme, setChoice };
}

export interface LogoPick {
  src: string;
  /** Đặt trên tấm nền trắng: chỉ khi nền tối mà công ty chưa có logo cho nền tối. */
  plate: boolean;
  /** Logo của công ty (false ⇒ logo mặc định Elite). */
  own: boolean;
}

/**
 * Logo theo giao diện: sáng ⇒ logo thường; tối ⇒ bản cho nền tối, chưa có thì logo thường trên tấm trắng. Thiếu bản phù
 * hợp ⇒ logo Elite theo nền (bản trắng không đọc được trên nền sáng nên không dùng ở giao diện sáng).
 */
export function pickLogo(t: { logoUrl?: string | null; logoDarkUrl?: string | null }, theme: Theme, fallback: { light: string; dark: string }): LogoPick {
  if (theme === 'dark') {
    if (t.logoDarkUrl) return { src: t.logoDarkUrl, plate: false, own: true };
    if (t.logoUrl) return { src: t.logoUrl, plate: true, own: true };
    return { src: fallback.dark, plate: false, own: false };
  }
  return t.logoUrl ? { src: t.logoUrl, plate: false, own: true } : { src: fallback.light, plate: false, own: false };
}
